import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { DollarSign, Users, Clock, ArrowLeftRight, MessageSquare, ChevronRight } from "lucide-react";

function fmt(n) { return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n); }

export default function Overview() {
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, u] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/users?status=pending"),
        ]);
        setStats(s.data);
        setPending((u.data.users || []).slice(0, 6));
      } catch (e) { console.error("Admin overview fetch error:", e); }
      finally { setLoading(false); }
    })();
  }, []);

  const kpis = stats ? [
    { label: "Total AUM", value: fmt(stats.total_aum), icon: DollarSign, color: "text-brand" },
    { label: "Active Clients", value: stats.active, icon: Users, color: "text-emerald-600" },
    { label: "Pending Approvals", value: stats.pending, icon: Clock, color: stats.pending > 0 ? "text-amber-600" : "text-muted-foreground" },
    { label: "Open Fund Requests", value: stats.pending_funds, icon: ArrowLeftRight, color: stats.pending_funds > 0 ? "text-blue-600" : "text-muted-foreground" },
  ] : [];

  return (
    <div data-testid="admin-overview" className="space-y-8">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">Overview</h1>
        <p className="mt-1 text-sm text-muted-foreground">Operational snapshot of MentorAI Capital.</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="rounded-xl ring-1 ring-black/5 dark:ring-white/5 shadow-sm p-5">
              <Skeleton className="h-4 w-24 mb-3" />
              <Skeleton className="h-8 w-20" />
            </Card>
          ))
        ) : (
          kpis.map((k, i) => (
            <Card key={i} data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-xl ring-1 ring-black/5 dark:ring-white/5 shadow-sm p-5 hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{k.label}</span>
                <k.icon className={`size-4 ${k.color}`} />
              </div>
              <div className="mt-2 text-2xl font-display font-semibold tabular-nums">{k.value}</div>
            </Card>
          ))
        )}
      </div>

      {/* Quick action row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Pending Approvals Queue */}
        <Card className="rounded-xl ring-1 ring-black/5 dark:ring-white/5 shadow-sm">
          <div className="flex items-center justify-between px-5 pt-5 pb-3">
            <h2 className="font-display text-lg font-semibold tracking-tight">Pending Approvals</h2>
            <Link to="/admin/clients">
              <Button variant="ghost" size="sm" className="text-xs gap-1" data-testid="overview-view-all-clients">View all <ChevronRight className="size-3.5" /></Button>
            </Link>
          </div>
          <div className="px-5 pb-5">
            {loading ? (
              <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
            ) : pending.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No pending approvals.</p>
            ) : (
              <div className="space-y-2">
                {pending.map((u) => (
                  <div key={u.user_id} data-testid={`pending-user-${u.user_id}`} className="flex items-center justify-between rounded-lg px-3 py-2.5 bg-muted/50 hover:bg-muted transition-colors duration-150">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{u.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                    </div>
                    <Badge variant="outline" className="rounded-full bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800 text-[10px] shrink-0">PENDING</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Quick Stats */}
        <Card className="rounded-xl ring-1 ring-black/5 dark:ring-white/5 shadow-sm">
          <div className="px-5 pt-5 pb-3">
            <h2 className="font-display text-lg font-semibold tracking-tight">Quick Stats</h2>
          </div>
          <div className="px-5 pb-5 space-y-4">
            {loading ? (
              <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-6 w-full" />)}</div>
            ) : (
              <>
                <div className="flex items-center justify-between py-2 border-b">
                  <span className="text-sm text-muted-foreground flex items-center gap-2"><Users className="size-4" /> Total Registered</span>
                  <span className="text-sm font-semibold font-display tabular-nums">{stats?.total || 0}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b">
                  <span className="text-sm text-muted-foreground flex items-center gap-2"><MessageSquare className="size-4" /> Unread Messages</span>
                  <span className="text-sm font-semibold font-display tabular-nums">{stats?.unread_messages || 0}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b">
                  <span className="text-sm text-muted-foreground flex items-center gap-2"><Clock className="size-4" /> Pending Approvals</span>
                  <span className="text-sm font-semibold font-display tabular-nums">{stats?.pending || 0}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-muted-foreground flex items-center gap-2"><ArrowLeftRight className="size-4" /> Open Fund Requests</span>
                  <span className="text-sm font-semibold font-display tabular-nums">{stats?.pending_funds || 0}</span>
                </div>
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
